import React, { useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import styled from '@emotion/styled';
import Papa from 'papaparse';

import {
  CenteredH2,
  ModalActions,
  ModalContent,
  ModalField,
  ModalOverlay,
  RadioGroup,
  RadioLabel,
  ScrollableContent,
} from './styles';
import ActionButton from '../../../components/ActionButton/ActionButton';
import { combineDateAndTimeToISO } from '../../../../helpers/dateTimeHelper';
import {
  cropConnectApiClient,
  CropConnectRoleDefinitions,
  CropConnectTaskDto,
  TaskType,
} from '../../../../services/cropConnectApiClient';
import { useI18n } from '../../../../hooks/useI18n';
import { TranslationKeys } from '../../../../i18n/locales/defaultResources';
import GeneralDocumentPicker from '../../../components/GeneralDocumentPicker/GeneralDocumentPicker';

const DateTimeRangePickerContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const DateTimePickerRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const Label = styled.label`
  // font-weight: bold;
`;

const MyTasksCreateModal = ({
  isOpen,
  onClose,
  task,
  onTaskSuccessfulUpsert,
}: {
  isOpen: boolean;
  onClose: () => void;
  task?: CropConnectTaskDto;
  onTaskSuccessfulUpsert: () => void;
}) => {
  const isEditing = !!task;
  const { t } = useI18n();

  const [headline, setHeadline] = useState('');
  const [description, setDescription] = useState('');
  const [taskType, setTaskType] = useState<TaskType>(TaskType.EXTERNAL_URL);
  const [externalUrl, setExternalUrl] = useState('');
  const [internalUrl, setInternalUrl] = useState('');
  const [selectedSurveyCsvFile, setSelectedSurveyCsvFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [generalDocumentEcmId, setGeneralDocumentEcmId] = useState<
    number | null
  >(null);
  const [parentFolder, setParentFolder] = useState<number | null>(null);
  const [generalDocumentEcmName, setGeneralDocumentEcmName] = useState<
    string | null
  >(null);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [startTime, setStartTime] = useState<string>('');
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [endTime, setEndTime] = useState<string>('');
  const [assignmentType, setAssignmentType] = useState<'ROLES' | 'SINGLE'>(
    'ROLES'
  );
  const [roles, setRoles] = useState<CropConnectRoleDefinitions[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<
    CropConnectRoleDefinitions[]
  >([]);
  const [bpNumbers, setBpNumbers] = useState<string>('');
  const [isHeadlineInvalid, setIsHeadlineInvalid] = useState(false);
  const [isStartDateInvalid, setIsStartDateInvalid] = useState(false);
  const [isStartTimeInvalid, setIsStartTimeInvalid] = useState(false);
  const [isEndDateInvalid, setIsEndDateInvalid] = useState(false);
  const [isEndTimeInvalid, setIsEndTimeInvalid] = useState(false);
  const [isSurveyCsvFileInvalid, setIsSurveyCsvFileInvalid] = useState(false);

  useEffect(() => {
    console.log(task);
    if (isEditing) {
      setHeadline(task.propertyBag?.headline);
      setDescription(task.propertyBag?.description);
      setTaskType(task.type);
      if (task.type === TaskType.EXTERNAL_URL)
        setExternalUrl(task.propertyBag?.externalUrl);
      if (task.type === TaskType.INTERNAL_URL)
        setInternalUrl(task.propertyBag?.internalUrl);
      if (task.type === TaskType.SURVEY)
        setCsvData(task.propertyBag?.surveyDataFromCsv ?? []);
      setStartDate(new Date(task.startDate ?? ''));
      setStartTime(new Date(task.startDate ?? '').toLocaleTimeString());
      setEndDate(new Date(task.endDate ?? ''));
      setEndTime(new Date(task.endDate ?? '').toLocaleTimeString());
      setAssignmentType(task.propertyBag?.assignmentType);
      setSelectedRoles(task.assignedToRoles ?? []);
      setBpNumbers(task.assignedToBusinessPartners?.join('\n') ?? '');
      setGeneralDocumentEcmId(task.propertyBag?.generalDocumentEcmId);
      setParentFolder(task.propertyBag?.parentFolderId);
      setGeneralDocumentEcmName(task.propertyBag?.generalDocumentEcmName);
    }
  }, [task, isEditing]);

  useEffect(() => {
    const fetchRoles = async () => {
      const roles = await cropConnectApiClient.applicableRoles();
      if (roles.data) {
        setRoles(roles.data);
      }
    };
    fetchRoles();
  }, [task]);

  const handleChange = (event) => {
    const { value, checked } = event.target;

    setSelectedRoles((prev) =>
      checked ? [...prev, value] : prev.filter((item) => item !== value)
    );
  };

  const resetState = () => {
    setHeadline('');
    setDescription('');
    setTaskType(TaskType.EXTERNAL_URL);
    setExternalUrl('');
    setInternalUrl('');
    setGeneralDocumentEcmId(null);
    setParentFolder(null);
    setGeneralDocumentEcmName(null);
    setStartDate(null);
    setStartTime('');
    setEndDate(null);
    setEndTime('');
    setAssignmentType('ROLES');
    setSelectedRoles([]);
    setBpNumbers('');
    setIsHeadlineInvalid(false);
    setIsStartDateInvalid(false);
    setIsStartTimeInvalid(false);
    setIsEndDateInvalid(false);
    setIsEndTimeInvalid(false);
    setIsSurveyCsvFileInvalid(false);
  };

  const handleSave = async () => {
    let isValid = true;

    if (!headline) {
      setIsHeadlineInvalid(true);
      isValid = false;
    }
    if (!startDate) {
      setIsStartDateInvalid(true);
      isValid = false;
    }
    if (!startTime) {
      setIsStartTimeInvalid(true);
      isValid = false;
    }
    if (!endDate) {
      setIsEndDateInvalid(true);
      isValid = false;
    }
    if (!endTime) {
      setIsEndTimeInvalid(true);
      isValid = false;
    }
    if (
      taskType === TaskType.SURVEY &&
      (!csvData.length || !selectedSurveyCsvFile)
    ) {
      setIsSurveyCsvFileInvalid(true);
      isValid = false;
    }

    if (!isValid) {
      return;
    }

    let bpNumbersArray: string[] = [];
    if (taskType === TaskType.SURVEY) {
      bpNumbersArray = csvData.map((row) => row.GrowerBp);
    } else {
      if (assignmentType === 'SINGLE') {
        bpNumbersArray = bpNumbers.split('\n');
      } else {
        bpNumbersArray = [];
      }
    }

    if (isEditing) {
      const taskId = task.accessId!;
      const updatedTask = await cropConnectApiClient.addTask({
        type: taskType,
        startDate: combineDateAndTimeToISO(startDate, startTime),
        endDate: combineDateAndTimeToISO(endDate, endTime),
        propertyBag: {
          headline,
          description,
          externalUrl: taskType === TaskType.EXTERNAL_URL ? externalUrl : null,
          internalUrl: taskType === TaskType.INTERNAL_URL ? internalUrl : null,
          surveyDataFromCsv: taskType === TaskType.SURVEY ? csvData : null,
          generalDocumentEcmId:
            taskType === TaskType.GENERAL_DOCUMENT
              ? generalDocumentEcmId
              : null,
          generalDocumentEcmName:
            taskType === TaskType.GENERAL_DOCUMENT
              ? generalDocumentEcmName
              : null,
          parentFolderId:
            taskType === TaskType.GENERAL_DOCUMENT ? parentFolder : null,
          assignmentType,
        },
        assignedToBusinessPartners: bpNumbersArray,
        assignedToRoles:
          assignmentType === 'ROLES'
            ? (selectedRoles as CropConnectRoleDefinitions[])
            : [],
      });
      if (updatedTask.success) {
        const deleteAfterAddedTask =
          await cropConnectApiClient.deleteTask(taskId);
        if (deleteAfterAddedTask.success) {
          onTaskSuccessfulUpsert();
        }
      }
    } else {
      const addedTasks = await cropConnectApiClient.addTask({
        type: taskType,
        startDate: combineDateAndTimeToISO(startDate, startTime),
        endDate: combineDateAndTimeToISO(endDate, endTime),
        propertyBag: {
          headline,
          description,
          externalUrl: taskType === TaskType.EXTERNAL_URL ? externalUrl : null,
          internalUrl: taskType === TaskType.INTERNAL_URL ? internalUrl : null,
          surveyDataFromCsv: taskType === TaskType.SURVEY ? csvData : null,
          generalDocumentEcmId:
            taskType === TaskType.GENERAL_DOCUMENT
              ? generalDocumentEcmId
              : null,
          generalDocumentEcmName:
            taskType === TaskType.GENERAL_DOCUMENT
              ? generalDocumentEcmName
              : null,
          parentFolderId:
            taskType === TaskType.GENERAL_DOCUMENT ? parentFolder : null,
          assignmentType,
        },
        assignedToBusinessPartners: bpNumbersArray,
        assignedToRoles:
          assignmentType === 'ROLES'
            ? (selectedRoles as CropConnectRoleDefinitions[])
            : [],
      });
      if (addedTasks.success) {
        onTaskSuccessfulUpsert();
      }
    }
    resetState();
    onClose();
  };

  const handleOnCancel = () => {
    resetState();
    onClose();
  };

  const handleStartDateChange = (date: Date | null) => {
    setStartDate(date);
    setIsStartDateInvalid(false);
  };

  const handleEndDateChange = (date: Date | null) => {
    setEndDate(date);
    setIsEndDateInvalid(false);
  };

  const handleOnSurveyCsvFileChange = (e) => {
    const file = e.target.files[0];
    setSelectedSurveyCsvFile(file);
    if (file) {
      const reader = new FileReader();
      reader.onload = ({ target }) => {
        const csv = Papa.parse(target.result, { header: true });
        setCsvData(
          csv.data
            .filter((row) => row.Username && row.Password && row.Grower)
            .map((row) => {
              return {
                ProjectId: row.Username,
                Password: row.Password,
                GrowerBp: row.Grower,
              };
            })
        );
      };
      reader.readAsText(file);
    }
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay>
      <style>
        {`
          .error-border {
            border: 1px solid red;
          }
        `}
      </style>
      <ModalContent>
        <CenteredH2>
          {t(TranslationKeys.my_tasks_create_modal_title)}
        </CenteredH2>
        <ScrollableContent>
          <ModalField>
            <label style={{ fontWeight: 'bold' }}>
              {t(TranslationKeys.my_tasks_create_modal_headline)}
            </label>
            <input
              type="text"
              value={headline}
              onChange={(e) => {
                setHeadline(e.target.value);
                setIsHeadlineInvalid(false);
              }}
              style={{ borderColor: isHeadlineInvalid ? 'red' : 'initial' }}
            />
          </ModalField>
          <ModalField>
            <label style={{ fontWeight: 'bold' }}>
              {t(TranslationKeys.my_tasks_create_modal_description)}
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </ModalField>
          <ModalField>
            <label style={{ fontWeight: 'bold' }}>
              {t(TranslationKeys.my_tasks_create_modal_task_type)}
            </label>
            <RadioGroup>
              <RadioLabel>
                <input
                  type="radio"
                  value={TaskType.EXTERNAL_URL}
                  checked={taskType === TaskType.EXTERNAL_URL}
                  onChange={(e) => setTaskType(e.target.value as TaskType)}
                />
                {t(TranslationKeys.my_tasks_create_modal_external_url)}
              </RadioLabel>
              <RadioLabel>
                <input
                  type="radio"
                  value={TaskType.INTERNAL_URL}
                  checked={taskType === TaskType.INTERNAL_URL}
                  onChange={(e) => setTaskType(e.target.value)}
                />
                {t(TranslationKeys.my_tasks_create_modal_internal_url)}
              </RadioLabel>
              <RadioLabel>
                <input
                  type="radio"
                  value={TaskType.ESHOP}
                  checked={taskType === TaskType.ESHOP}
                  onChange={(e) => setTaskType(e.target.value as TaskType)}
                />
                {t(TranslationKeys.my_tasks_create_modal_eshop)}
              </RadioLabel>
              <RadioLabel>
                <input
                  type="radio"
                  value={TaskType.ECONTRACTING}
                  checked={taskType === TaskType.ECONTRACTING}
                  onChange={(e) => setTaskType(e.target.value as TaskType)}
                />
                {t(TranslationKeys.my_tasks_create_modal_econtracting)}
              </RadioLabel>
              <RadioLabel>
                <input
                  type="radio"
                  value={TaskType.FIELD_MAP}
                  checked={taskType === TaskType.FIELD_MAP}
                  onChange={(e) => setTaskType(e.target.value as TaskType)}
                />
                {t(TranslationKeys.my_tasks_create_modal_field_map)}
              </RadioLabel>
              <RadioLabel>
                <input
                  type="radio"
                  value={TaskType.FILL_RED_CERT_SURVEY}
                  checked={taskType === TaskType.FILL_RED_CERT_SURVEY}
                  onChange={(e) => setTaskType(e.target.value as TaskType)}
                />
                {t(TranslationKeys.my_tasks_create_modal_redcert)}
              </RadioLabel>
              <RadioLabel>
                <input
                  type="radio"
                  value={TaskType.GENERAL_DOCUMENT}
                  checked={taskType === TaskType.GENERAL_DOCUMENT}
                  onChange={(e) => setTaskType(e.target.value as TaskType)}
                />
                {t(TranslationKeys.my_tasks_create_modal_general_document)}
              </RadioLabel>
              <RadioLabel>
                <input
                  type="radio"
                  value={TaskType.SURVEY}
                  checked={taskType === TaskType.SURVEY}
                  onChange={(e) => setTaskType(e.target.value as TaskType)}
                />
                {t(TranslationKeys.my_tasks_create_modal_survey)}
              </RadioLabel>
            </RadioGroup>
          </ModalField>
          {taskType === TaskType.EXTERNAL_URL && (
            <ModalField>
              <label style={{ fontWeight: 'bold' }}>
                {t(TranslationKeys.my_tasks_create_modal_external_url_input)}
              </label>
              <input
                type="text"
                value={externalUrl}
                onChange={(e) => setExternalUrl(e.target.value)}
              />
            </ModalField>
          )}
          {taskType === TaskType.INTERNAL_URL && (
            <ModalField>
              <label style={{ fontWeight: 'bold' }}>
                {t(TranslationKeys.my_tasks_create_modal_internal_url_input)}
              </label>
              <input
                type="text"
                value={internalUrl}
                onChange={(e) => setInternalUrl(e.target.value)}
              />
            </ModalField>
          )}
          {taskType === TaskType.GENERAL_DOCUMENT && (
            <ModalField>
              <div>
                {generalDocumentEcmName
                  ? generalDocumentEcmName
                  : t(
                      TranslationKeys.create_general_document_modal_choose_document
                    )}
              </div>
              <GeneralDocumentPicker
                onGeneralDocumentPicked={(
                  generalDocumentEcmId,
                  parentFolderId,
                  generalDocumentEcmName
                ) => {
                  setGeneralDocumentEcmId(generalDocumentEcmId);
                  setParentFolder(parentFolderId);
                  setGeneralDocumentEcmName(generalDocumentEcmName);
                }}
              />
            </ModalField>
          )}
          {taskType === TaskType.SURVEY && (
            <ModalField>
              <label style={{ fontWeight: 'bold' }}>
                {t(TranslationKeys.my_tasks_create_modal_survey_csv_input)}
              </label>
              <input
                type="file"
                accept=".csv"
                onChange={(e) => {
                  handleOnSurveyCsvFileChange(e);
                  setIsSurveyCsvFileInvalid(false);
                }}
              />
              {isSurveyCsvFileInvalid && (
                <div style={{ color: 'red' }}>
                  {t(TranslationKeys.my_tasks_csv_invalid_warning_message)}
                </div>
              )}
            </ModalField>
          )}

          <DateTimeRangePickerContainer>
            <DateTimePickerRow>
              <Label>
                {t(TranslationKeys.my_tasks_create_modal_valid_from)}
              </Label>
              <DatePicker
                selected={startDate}
                onChange={handleStartDateChange}
                dateFormat="yyyy-MM-dd"
                className={isStartDateInvalid ? 'error-border' : ''}
              />
              <Label>
                {t(TranslationKeys.my_tasks_create_modal_valid_from_time)}
              </Label>
              <input
                type="time"
                value={startTime}
                onChange={(e) => {
                  setStartTime(e.target.value);
                  setIsStartTimeInvalid(false);
                }}
                style={{ borderColor: isStartTimeInvalid ? 'red' : undefined }}
              />
            </DateTimePickerRow>
            <DateTimePickerRow>
              <Label>{t(TranslationKeys.my_tasks_create_modal_valid_to)}</Label>
              <DatePicker
                selected={endDate}
                minDate={startDate}
                onChange={handleEndDateChange}
                dateFormat="yyyy-MM-dd"
                className={isEndDateInvalid ? 'error-border' : ''}
              />
              <Label>
                {t(TranslationKeys.my_tasks_create_modal_valid_to_time)}
              </Label>
              <input
                type="time"
                value={endTime}
                onChange={(e) => {
                  setEndTime(e.target.value);
                  setIsEndTimeInvalid(false);
                }}
                style={{ borderColor: isEndTimeInvalid ? 'red' : undefined }}
              />
            </DateTimePickerRow>
          </DateTimeRangePickerContainer>

          {taskType !== TaskType.SURVEY && (
            <div>
              <ModalField>
                <label style={{ fontWeight: 'bold' }}>
                  {t(TranslationKeys.my_tasks_create_modal_assignment_type)}
                </label>
                <RadioGroup>
                  <RadioLabel>
                    <input
                      type="radio"
                      value="ROLES"
                      checked={assignmentType === 'ROLES'}
                      onChange={(e) => setAssignmentType(e.target.value)}
                    />
                    {t(TranslationKeys.my_tasks_create_modal_roles)}
                  </RadioLabel>
                  <RadioLabel>
                    <input
                      type="radio"
                      value="SINGLE"
                      checked={assignmentType === 'SINGLE'}
                      onChange={(e) => setAssignmentType(e.target.value)}
                    />
                    {t(TranslationKeys.my_tasks_create_modal_single)}
                  </RadioLabel>
                </RadioGroup>
              </ModalField>

              {assignmentType === 'ROLES' && (
                <ModalField>
                  <label style={{ fontWeight: 'bold' }}>
                    {t(TranslationKeys.my_tasks_create_modal_roles_select)}
                  </label>
                  {roles.map((role) => (
                    <div key={role} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id={role}
                        value={role}
                        checked={selectedRoles.includes(role)}
                        onChange={handleChange}
                        className="w-4 h-4"
                      />
                      <label htmlFor={role} style={{ marginLeft: '4px' }}>
                        {role}
                      </label>
                    </div>
                  ))}
                </ModalField>
              )}

              {assignmentType === 'SINGLE' && (
                <ModalField>
                  <label style={{ fontWeight: 'bold' }}>
                    {t(TranslationKeys.my_tasks_create_modal_single_input)}
                  </label>
                  <textarea
                    value={bpNumbers}
                    onChange={(e) => setBpNumbers(e.target.value)}
                    rows="5"
                  />
                </ModalField>
              )}
            </div>
          )}
        </ScrollableContent>
        <ModalActions>
          <ActionButton
            variant="accept-blue"
            buttonText={t(TranslationKeys.my_tasks_create_modal_cancel)}
            onClickAction={handleOnCancel}
          />{' '}
          <ActionButton
            variant="accept-blue"
            buttonText={
              isEditing
                ? t(TranslationKeys.my_tasks_create_modal_save)
                : t(TranslationKeys.my_tasks_create_modal_create)
            }
            onClickAction={handleSave}
          />
        </ModalActions>
      </ModalContent>
    </ModalOverlay>
  );
};

export default MyTasksCreateModal;
